import { updateEvent, loadEvents } from '../../services/eventService';
import { updateCalculs } from '../../services/calculService';
import { loadPeriods } from '../../services/periodService';
import { getElementDate } from '../../ui-tools/dateUtils';
import { createEventElement } from './EventItem';
import { createPeriodSeparator, createTodaySeparator } from './PeriodSeparator';
import { createYearHeader } from './YearHeader';
import { createMonthHeader } from './MonthHeader';

// Fonction d'initialisation de la liste des événements
export async function initEventList() {
  try {
    // Récupérer le conteneur d'événements
    const eventsContainer = document.querySelector('.events-container');

    // Vérifier que l'élément existe avant d'y accéder
    if (!eventsContainer) {
      console.error("Le conteneur d'événements n'existe pas");
      return;
    }

    // Charger les événements et la période
    const events = await loadEvents();
    const periods = await loadPeriods();

    // Si pas d'événements, afficher un message
    if (!events || events.length === 0) {
      eventsContainer.innerHTML = '<p class="no-events">Aucun événement à afficher</p>';
      return;
    }

    // Vider le conteneur
    eventsContainer.innerHTML = '';

    // Variables pour stocker les dates de période
    let periodStart = null;
    let periodEnd = null;

    // Récupérer la période si elle existe
    if (periods && periods.length > 0) {
      periodStart = new Date(periods[0].startDate);
      periodEnd = new Date(periods[0].endDate);
    }

    // Marquer les événements qui sont dans la période
    if (periodStart && periodEnd) {
      events.forEach(event => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);

        // Ajuster la date de fin de période pour inclure tout le jour
        const adjustedPeriodEnd = new Date(periodEnd);
        adjustedPeriodEnd.setHours(23, 59, 59, 999);

        // Un événement est dans la période si :
        // - sa date de fin est >= début de période ET
        // - sa date de début est <= fin de période ajustée (pour inclure tout le jour)
        event.isInPeriod = (eventEnd >= periodStart && eventStart <= adjustedPeriodEnd);
      });
    }

    // Créer la timeline avec tous les éléments chronologiques
    const timeline = [];

    // Collecter les années uniques
    const years = new Set();
    events.forEach(event => {
      const date = new Date(event.startDate);
      years.add(date.getFullYear());
    });

    // Ajouter le séparateur de début de période s'il existe
    if (periodStart) {
      timeline.push({
        type: 'period-separator',
        date: periodStart,
        data: {
          date: periodStart,
          isStart: true
        }
      });
    }

    // Ajouter le séparateur "Aujourd'hui"
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    timeline.push({
      type: 'today-separator',
      date: today,
      data: {
        date: today
      }
    });

    // Ajouter tous les événements
    events.forEach(event => {
      const eventDate = new Date(event.startDate);
      timeline.push({
        type: 'event',
        date: eventDate,
        data: event
      });
    });

    // Ajouter le séparateur de fin de période s'il existe
    if (periodEnd) {
      timeline.push({
        type: 'period-separator',
        date: periodEnd,
        data: {
          date: periodEnd,
          isStart: false
        }
      });
    }

    // Fonction helper pour obtenir la date de tri d'un élément de timeline
    function getTimelineSortDate(timelineItem) {
      switch (timelineItem.type) {
        case 'period-separator':
          const isEndSeparator = timelineItem.data.isStart === false;
          return getElementDate('separator', timelineItem.date, isEndSeparator);
        case 'today-separator':
          return getElementDate('today-separator', timelineItem.date, false);
        case 'event':
          return getElementDate('event', timelineItem.date, false);
        default:
          return timelineItem.date;
      }
    }

    // Trier la timeline par date avec les heures fines
    timeline.sort((a, b) => getTimelineSortDate(a) - getTimelineSortDate(b));

    // Structures pour stocker les éléments générés
    const yearsMap = new Map();
    const separators = [];

    // Organiser les éléments dans la structure hiérarchique
    // Remplacer la boucle forEach par une boucle for...of qui supporte mieux l'async/await
    for (const item of timeline) {
      if (item.type === 'period-separator') {
        separators.push({
          element: createPeriodSeparator(item.data),
          date: item.date
        });
      } else if (item.type === 'today-separator') {
        separators.push({
          element: createTodaySeparator(),
          date: item.date,
          isToday: true
        });
      } else if (item.type === 'event') {
        const date = new Date(item.date);
        const year = date.getFullYear();
        const month = date.getMonth();
        const event = item.data;

        // Déterminer si l'événement est passé ou futur hors période
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const eventStartDate = new Date(event.startDate);
        eventStartDate.setHours(0, 0, 0, 0);

        const eventEndDate = new Date(event.endDate);
        eventEndDate.setHours(0, 0, 0, 0);

        // Un événement est considéré comme passé uniquement si sa date de fin est antérieure à aujourd'hui
        const isPast = eventEndDate < today;
        const isInPeriod = event.isInPeriod;

        // Si l'événement est passé, le confirmer automatiquement, indépendamment du fait qu'il soit dans la période ou non
        if (isPast && event.status !== 'confirmed' && event.status !== 'option-') {
          event.status = 'confirmed'; // Mettre à jour le statut dans l'objet de données

          // Mettre à jour l'événement dans la base de données
          try {
            // Assurez-vous de passer l'ID et l'événement complet, pas seulement l'ID
            await updateEvent(event.id, event);
          } catch (error) {
            console.error('Erreur lors de la mise à jour du statut de l\'événement:', error);
          }
        }

        // Créer l'élément d'événement
        const eventElement = await createEventElement(event); // <-- AJOUTE await

        // Supprimer toutes les classes potentiellement conflictuelles
        if (eventElement && eventElement.classList) {
          eventElement.classList.remove('past', 'future', 'past-in-period', 'hors-periode', 'status-ok', 'status-option-plus', 'status-option-moins');
        }

        // Application des classes temporelles et de période
        if (eventElement && eventElement.classList) {
          if (isPast) {
            eventElement.classList.add('past');
          } else {
            eventElement.classList.add('future');
          }

          // Application mutuellement exclusive des classes de période et statut
          if (!isInPeriod) {
            eventElement.classList.add('hors-periode');
          } else if (isPast) {
            eventElement.classList.add('past-in-period');
            eventElement.classList.add('status-ok');
          } else {
            if (event.status === 'confirmed') {
              eventElement.classList.add('status-ok');
            } else if (event.status === 'option-plus') {
              eventElement.classList.add('status-option-plus');
            } else if (event.status === 'option-moins') {
              eventElement.classList.add('status-option-moins');
            }
          }
        }

        // Créer la structure d'année si nécessaire
        if (!yearsMap.has(year)) {
          const yearElement = createYearHeader(year);
          yearsMap.set(year, {
            element: yearElement,
            months: new Map(),
            date: new Date(year, 0, 1)
          });
        }

        const yearData = yearsMap.get(year);

        // Créer la structure de mois si nécessaire
        if (!yearData.months.has(month)) {
          const monthElement = createMonthHeader(month, year);
          yearData.months.set(month, {
            element: monthElement,
            events: [],
            date: new Date(year, month, 1)
          });
        }

        // Ajouter l'événement au mois correspondant
        const monthData = yearData.months.get(month);
        monthData.events.push({
          element: eventElement,
          date: item.date
        });
      }
    }

    // Construire une timeline unifiée avec séparateurs
    const allElements = [];

    // Ajouter les séparateurs
    separators.forEach(separator => {
      // Détecter s'il s'agit d'un séparateur de fin en vérifiant la classe ou les données
      const isEndSeparator = separator.element.classList.contains('period-end-separator');
      const isTodaySeparator = separator.isToday === true;

      allElements.push({
        type: 'separator',
        element: separator.element,
        date: separator.date,
        sortDate: isTodaySeparator
          ? getElementDate('today-separator', separator.date, false)
          : getElementDate('separator', separator.date, isEndSeparator),
        isEndSeparator: isEndSeparator,
        isTodaySeparator: isTodaySeparator
      });
    });

    // Ajouter les autres structures (années, mois, événements)
    for (const [year, yearData] of yearsMap.entries()) {
      allElements.push({
        type: 'year',
        element: yearData.element,
        date: yearData.date,
        sortDate: getElementDate('year', yearData.date),
        year: year
      });

      for (const [month, monthData] of yearData.months.entries()) {
        allElements.push({
          type: 'month',
          element: monthData.element,
          date: monthData.date,
          sortDate: getElementDate('month', monthData.date),
          year: year,
          month: month,
          parent: yearData.element
        });

        // Pour chaque mois, ajouter ses événements
        monthData.events.forEach(eventItem => {
          allElements.push({
            type: 'event',
            element: eventItem.element,
            date: eventItem.date,
            sortDate: getElementDate('event', eventItem.date),
            year: year,
            month: month,
            parentMonth: monthData.element
          });
        });
      }
    }

    // Trier les éléments chronologiquement
    allElements.sort((a, b) => {
      // D'abord comparer par date
      const dateComparison = a.sortDate - b.sortDate;
      if (dateComparison !== 0) return dateComparison;

      // Si les dates sont identiques, prioriser par type
      if (a.type !== b.type) {
        // CORRECTION : Fonction de priorité statique et cohérente
        const getTypePriority = (element) => {
          if (element.type === 'year') return 1;
          if (element.type === 'month') return 2;
          if (element.type === 'separator') {
            // GESTION SPÉCIFIQUE des différents types de séparateurs
            if (element.isTodaySeparator) return 2.5;
            if (element.isEndSeparator) return 5;
            return 3; // séparateurs de période normaux
          }
          if (element.type === 'event') return 4;
          return 6;
        };

        const priorityA = getTypePriority(a);
        const priorityB = getTypePriority(b);

        return priorityA - priorityB;
      }

      // Si même type, garder l'ordre d'origine
      return 0;
    });

    // Construire la structure DOM
    const eventsList = document.getElementById('events-list') || eventsContainer.querySelector('#events-list');
    if (!eventsList) {
      // Créer events-list s'il n'existe pas
      const newEventsList = document.createElement('div');
      newEventsList.id = 'events-list';
      eventsContainer.appendChild(newEventsList);
    }

    eventsContainer.innerHTML = '';

    // Recréer la structure de base

    const newEventsList = document.createElement('div');
    newEventsList.id = 'events-list';
    eventsContainer.appendChild(newEventsList);

    // === AJOUTER LE LIEN "Aller à aujourd'hui" DANS LE BON CONTENEUR ===
    const todayLink = document.createElement('a');
    todayLink.href = '#today';
    todayLink.className = 'scroll-to-today absolute-today-link fade-in';
    todayLink.textContent = "Aujourd'hui";

    // L'ajouter dans events-container, pas dans events-list
    eventsContainer.appendChild(todayLink);

    // fonction de scrolling pour #today
    todayLink.addEventListener('click', function(e) {
      e.preventDefault();
      console.log('🎯 Clic sur le lien aujourd\'hui');

      const todayElem = document.getElementById('today');
      if (todayElem) {
        todayElem.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Ajouter une classe pour le style
        todayElem.classList.add('highlighted');

        // Supprimer la classe après l'animation
        setTimeout(() => {
          todayElem.classList.remove('highlighted');
        }, 3000);

        console.log('✅ Défilement vers aujourd\'hui effectué');
      } else {
        console.warn('⚠️ Élément #today non trouvé');
      }
    });


    // Variables pour suivre la structure courante
    let currentYear = null;
    let currentYearElement = null;
    let currentMonth = null;
    let currentMonthElement = null;

    // Parcourir les éléments chronologiquement et les ajouter à newEventsList
    allElements.forEach(item => {
      const itemDate = new Date(item.date);
      const itemYear = itemDate.getFullYear();
      const itemMonth = itemDate.getMonth();

      if (item.type === 'year') {
        newEventsList.appendChild(item.element);
        // Si nous changeons d'année, créer un nouvel élément année
        if (currentYear !== itemYear) {
          // Nouvelle année
          if (yearsMap.has(itemYear)) {
            currentYearElement = yearsMap.get(itemYear).element;
            eventsContainer.appendChild(currentYearElement);
          }
          currentYear = itemYear;
          currentMonth = null;
          currentMonthElement = null;
        }
      } else if (item.type === 'month') {
        // Si nous changeons de mois, créer un nouvel élément mois
        if (currentYear === itemYear && currentMonth !== itemMonth) {
          if (currentYearElement && yearsMap.get(itemYear).months.has(itemMonth)) {
            currentMonthElement = yearsMap.get(itemYear).months.get(itemMonth).element;
            currentYearElement.appendChild(currentMonthElement);
          }
          currentMonth = itemMonth;
        }
      } else if (item.type === 'separator') {
        // CORRECTION : Positionner le séparateur selon sa logique chronologique
        if (item.isTodaySeparator) {
          // Le séparateur "aujourd'hui" doit être inséré dans le bon mois/année
          if (currentYear === itemYear && currentMonth === itemMonth && currentMonthElement) {
            currentMonthElement.appendChild(item.element);
          } else if (currentYearElement && currentYear === itemYear) {
            // Si pas de mois courant mais bonne année, insérer avant les mois
            currentYearElement.appendChild(item.element);
          } else {
            // Sinon, insérer dans newEventsList mais dans l'ordre chronologique
            newEventsList.appendChild(item.element);
          }
        } else {
          // Pour les autres séparateurs (période), garder la logique existante
          if (currentMonthElement && itemYear === currentYear && itemMonth === currentMonth) {
            currentMonthElement.appendChild(item.element);
          } else {
            newEventsList.appendChild(item.element);
          }
        }
      } else if (item.type === 'event') {
        // Ajouter l'événement au mois courant
        if (currentMonthElement && itemYear === currentYear && itemMonth === currentMonth) {
          currentMonthElement.appendChild(item.element);
        }
      }
    });

    // Ajouter la détection de défilement après la création complète de la structure DOM
    setupStickyDetection();

    // // === DÉFILEMENT AUTOMATIQUE VERS AUJOURD'HUI AU CHARGEMENT ===
    // // Attendre que le DOM soit complètement rendu
    // setTimeout(() => {
    //   const todayElement = document.getElementById('today');
    //   if (todayElement) {
    //     console.log('🎯 Défilement automatique vers aujourd\'hui');
    //     todayElement.scrollIntoView({
    //       behavior: 'smooth',
    //       block: 'center'
    //     });

    //     // Ajouter un effet visuel temporaire
    //     todayElement.classList.add('highlighted');
    //     setTimeout(() => {
    //       todayElement.classList.remove('highlighted');
    //     }, 2000);
    //   } else {
    //     console.warn('⚠️ Séparateur aujourd\'hui non trouvé pour le défilement automatique');
    //   }
    // }, 200); // Délai pour s'assurer que le DOM est prêt

    // === CONFIGURER L'INTERSECTION OBSERVER ===
    const todaySeparator = document.getElementById('today');

    if (todaySeparator && todayLink) {

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            console.log('📍 Today separator visibility:', {
              isIntersecting: entry.isIntersecting,
              intersectionRatio: entry.intersectionRatio
            });

            if (entry.isIntersecting) {
              // Séparateur visible → faire disparaître le lien
              console.log('👁️ Today visible - masquer le lien');
              todayLink.classList.remove('fade-in');
              todayLink.classList.add('fade-out');
            } else {
              // Séparateur non visible → faire apparaître le lien
              console.log('🚫 Today non visible - afficher le lien');
              todayLink.classList.remove('fade-out');
              todayLink.classList.add('fade-in');
            }
          });
        },
        {
          threshold: 0.1
        }
      );

      observer.observe(todaySeparator);
      console.log('✅ Observer configuré pour #today');
    }

    // Mettre à jour les calculs
    await updateCalculs();
  } catch (error) {
    console.error('Erreur lors du chargement des événements:', error);
  }
}

// ❌ SUPPRIMER ENTIÈREMENT cette fonction dupliquée
/*
function setupTodayButtonVisibility() {
  const todaySection = document.getElementById('today');
  const todayButton = document.querySelector('.absolute-today-link');

  if (!todaySection || !todayButton) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Masquer le bouton quand "aujourd'hui" est visible
        todayButton.classList.add('fade-out');
        todayButton.classList.remove('fade-in');
      } else {
        // Afficher le bouton quand "aujourd'hui" n'est pas visible
        todayButton.classList.remove('fade-out');
        todayButton.classList.add('fade-in');
      }
    });
  }, {
    threshold: 0.1 // Se déclenche quand 10% de la section est visible
  });

  observer.observe(todaySection);
}
*/
function setupStickyDetection() {
  const h3Elements = document.querySelectorAll('.month-header h3');
  console.log('H3 trouvés pour sticky:', h3Elements.length);

  h3Elements.forEach(h3 => {
    // Créer un sentinel invisible juste avant le h3
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.position = 'absolute';
    sentinel.style.top = '0';
    sentinel.style.left = '0';
    sentinel.style.right = '0';
    sentinel.style.pointerEvents = 'none';

    const monthHeader = h3.closest('.month-header');
    monthHeader.style.position = 'relative';
    monthHeader.insertBefore(sentinel, h3);

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        h3.classList.add('is-sticky');
      } else {
        h3.classList.remove('is-sticky');
      }
    }, {
      threshold: 0,
      rootMargin: '0px 0px -1px 0px'
    });

    observer.observe(sentinel);
  });
}

/**
 * Génère la liste des événements triés par date
 * @param {Array} events - Les événements à afficher
 */
async function renderEventList(events) {
  const eventsList = document.getElementById('events-list');

  // Vider la liste actuelle
  eventsList.innerHTML = '';

  if (events.length === 0) {
    eventsList.innerHTML = '<p class="no-events">Aucun événement pour cette période.</p>';
    return;
  }

  // Trier les événements par date (du plus ancien au plus récent)
  events.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  // Date actuelle (à minuit pour comparer seulement les dates)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let currentDateDisplayed = false;

  // Parcourir les événements triés
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const eventDate = new Date(event.startDate);
    eventDate.setHours(0, 0, 0, 0);

    // Si on n'a pas encore affiché le marqueur de date actuelle
    // et que cet événement est aujourd'hui ou plus tard
    if (!currentDateDisplayed && eventDate >= today) {
      // Si l'événement est exactement aujourd'hui, on place le marqueur avant
      if (eventDate.getTime() === today.getTime()) {
        // Créer le marqueur de date actuelle
        const currentDateMarker = document.createElement('div');
        currentDateMarker.className = 'current-date-marker';
        currentDateMarker.innerHTML = '<span>Aujourd\'hui</span>';
        eventsList.appendChild(currentDateMarker);
        currentDateDisplayed = true;
      }
      // Si l'événement est après aujourd'hui et l'événement précédent avant aujourd'hui
      else if (i > 0) {
        const prevEventDate = new Date(events[i-1].startDate);
        prevEventDate.setHours(0, 0, 0, 0);

        if (prevEventDate < today) {
          // Créer le marqueur de date actuelle
          const currentDateMarker = document.createElement('div');
          currentDateMarker.className = 'current-date-marker';
          currentDateMarker.innerHTML = '<span>Aujourd\'hui</span>';
          eventsList.appendChild(currentDateMarker);
          currentDateDisplayed = true;
        }
      }
    }

    // Créer et ajouter l'élément d'événement
    const eventElement = createEventElement(event);

    // Utilisez des noms de variables différents pour éviter les redéclarations
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const currentEventStartDate = new Date(event.startDate);
    currentEventStartDate.setHours(0, 0, 0, 0);

    const currentEventEndDate = new Date(event.endDate);
    currentEventEndDate.setHours(0, 0, 0, 0);

    // Un événement est considéré comme passé uniquement si sa date de fin est antérieure à aujourd'hui
    const isPast = currentEventEndDate < currentDate;
    const isInPeriod = event.isInPeriod;

    // Si l'événement est passé, le confirmer automatiquement
    if (isPast && event.status !== 'confirmed' && event.status !== 'option-') {
      event.status = 'confirmed'; // Mettre à jour le statut dans l'objet de données

      // Mettre à jour l'événement dans la base de données
      try {
        // Assurez-vous de passer l'ID et l'événement complet, pas seulement l'ID
        await updateEvent(event.id, event);
      } catch (error) {
        console.error('Erreur lors de la mise à jour du statut de l\'événement:', error);
      }
    }

    // Supprimer toutes les classes potentiellement conflictuelles
    eventElement.classList.remove('past-in-period', 'hors-periode', 'status-ok', 'status-option-plus', 'status-option-moins');

    // Application mutuellement exclusive des classes
    if (!isInPeriod) {
      // Événements hors période (passés ou futurs)
      eventElement.classList.add('hors-periode');
    } else if (isPast) {
      // Événements dans la période qui sont passés
      eventElement.classList.add('past-in-period');
      // Ajouter également la classe de style pour les événements confirmés
      eventElement.classList.add('status-ok');
    } else {
      // Pour les événements futurs, appliquer la classe de statut appropriée
      if (event.status === 'confirmed') {
        eventElement.classList.add('status-ok');
      } else if (event.status === 'option-plus') {
        eventElement.classList.add('status-option-plus');
      } else if (event.status === 'option-moins') {
        eventElement.classList.add('status-option-moins');
      }
    }

    eventsList.appendChild(eventElement);
  }

  // Si on n'a pas encore affiché le marqueur de date actuelle
  // et qu'il n'y a pas d'événements futurs, ajouter le marqueur à la fin
  if (!currentDateDisplayed) {
    const currentDateMarker = document.createElement('div');
    currentDateMarker.className = 'current-date-marker';
    currentDateMarker.innerHTML = '<span>Aujourd\'hui</span>';
    eventsList.appendChild(currentDateMarker);
  }
}
